import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { IconButtonComponent } from '../button/icon-button.component';
import { IconComponent } from '../icon/icon.component';
import { Eye, EyeOff, Lock } from '../icon/icons';

let nextId = 0;

/**
 * Campo con etiqueta visible. Funciona con `[(value)]` y con formularios reactivos. La ayuda y el error
 * se ligan con `aria-describedby`, y el error también se dice con `aria-invalid`, no sólo con color.
 *
 * `locked` es el dato que define la empresa (nombre, correo…): se lee, se enfoca y se anuncia como de
 * sólo lectura, con el candado a la vista.
 */
@Component({
  selector: 'ui-text-field',
  imports: [IconButtonComponent, IconComponent],
  template: `
    <label class="label" [attr.for]="inputId">{{ label() }}</label>
    <div class="control" [class.locked]="locked()" [class.invalid]="!!error()">
      @if (multiline()) {
        <textarea
          #control
          class="input area"
          rows="3"
          [id]="inputId"
          [value]="value()"
          [disabled]="isDisabled()"
          [attr.maxlength]="maxLength() ?? null"
          [attr.placeholder]="placeholder() ?? null"
          [attr.aria-invalid]="error() ? 'true' : null"
          [attr.aria-describedby]="describedBy()"
          (input)="onInput($event)"
          (blur)="onTouched()"
        ></textarea>
      } @else {
        <input
          #control
          class="input"
          [id]="inputId"
          [type]="effectiveType()"
          [value]="value()"
          [readOnly]="locked()"
          [disabled]="isDisabled()"
          [attr.autocapitalize]="type() === 'email' ? 'none' : null"
          [attr.inputmode]="inputMode() ?? null"
          [attr.autocomplete]="autocomplete() ?? null"
          [attr.placeholder]="placeholder() ?? null"
          [attr.required]="required() ? '' : null"
          [attr.aria-invalid]="error() ? 'true' : null"
          [attr.aria-describedby]="describedBy()"
          (input)="onInput($event)"
          (blur)="onTouched()"
        />
      }
      @if (locked()) {
        <ui-icon class="lock" [icon]="lockIcon" size="1rem" />
      }
      @if (revealable()) {
        <button uiIconButton label="Mostrar contraseña" [pressed]="revealed()" (click)="revealed.set(!revealed())">
          <ui-icon [icon]="revealed() ? hideIcon : showIcon" size="1.25rem" />
        </button>
      }
    </div>
    @if (hint(); as hintText) {
      <p class="hint" [id]="hintId">{{ hintText }}</p>
    }
    @if (error(); as errorText) {
      <p class="error" [id]="errorId">{{ errorText }}</p>
    }
  `,
  styleUrl: './text-field.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TextFieldComponent), multi: true }],
})
export class TextFieldComponent implements ControlValueAccessor {
  readonly label = input.required<string>();
  readonly value = model('');
  readonly type = input<'text' | 'email' | 'tel' | 'password' | 'number'>('text');
  readonly inputMode = input<string>();
  readonly autocomplete = input<string>();
  readonly placeholder = input<string>();
  readonly hint = input<string>();
  readonly error = input<string | null>();
  readonly locked = input(false, { transform: booleanAttribute });
  readonly required = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  /** Botón para ver la contraseña: reduce errores al escribirla, sobre todo en un teléfono. */
  readonly revealable = input(false, { transform: booleanAttribute });
  /** Área de texto, para comentarios: crece con el texto del sistema porque sus renglones van en rem. */
  readonly multiline = input(false, { transform: booleanAttribute });
  readonly maxLength = input<number>();

  private readonly formDisabled = signal(false);
  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());

  private readonly id = nextId++;
  protected readonly inputId = `ui-field-${this.id}`;
  protected readonly hintId = `ui-field-${this.id}-hint`;
  protected readonly errorId = `ui-field-${this.id}-error`;
  protected readonly lockIcon = Lock;
  protected readonly showIcon = Eye;
  protected readonly hideIcon = EyeOff;
  protected readonly revealed = signal(false);
  protected readonly effectiveType = computed(() => (this.revealable() && this.revealed() ? 'text' : this.type()));

  private readonly control = viewChild.required<ElementRef<HTMLInputElement | HTMLTextAreaElement>>('control');

  protected readonly describedBy = computed(() => {
    const ids = [this.hint() ? this.hintId : null, this.error() ? this.errorId : null].filter(Boolean);
    return ids.length ? ids.join(' ') : null;
  });

  private onChange: (value: string) => void = () => undefined;
  protected onTouched: () => void = () => undefined;

  /** Para llevar el foco al primer campo con error (WCAG 3.3.1). */
  focus(): void {
    this.control().nativeElement.focus();
  }

  protected onInput(event: Event): void {
    const next = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    this.value.set(next);
    this.onChange(next);
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }
}
